import Image from 'next/image';
import { Calendar, Clock } from 'lucide-react';
import { BrandSparkle } from '@/components/brand/ornaments';
import { TypographyCaption } from '@/components/ui/typography';
import type { BlogCategory } from '@/content/blog-types';
import { cn } from '@/lib/utils';

export interface BlogCard {
  slug: string;
  title: string;
  description: string;
  category: BlogCategory;
  categoryLabel: string;
  image: string;
  /** ISO date for the <time> machine value. */
  date: string;
  dateLabel: string;
  minReadLabel: string;
}

interface BlogCardMediaProps {
  post: Pick<BlogCard, 'image'>;
  sizes: string;
  className?: string;
  imageClassName?: string;
}

export function BlogCardMedia({ post, sizes, className, imageClassName }: BlogCardMediaProps) {
  return (
    <div
      className={cn(
        'relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-secondary/30',
        className,
      )}
    >
      {post.image ? (
        <Image
          src={post.image}
          alt=""
          fill
          draggable={false}
          sizes={sizes}
          className={cn('select-none object-cover', imageClassName)}
        />
      ) : (
        <span className="absolute inset-0 grid place-items-center text-brand-gold/70" aria-hidden>
          <BrandSparkle className="h-10 w-10" />
        </span>
      )}
    </div>
  );
}

export function BlogCardMeta({ post, className }: { post: BlogCard; className?: string }) {
  return (
    <TypographyCaption
      className={cn('flex items-center gap-3 font-medium text-muted-foreground', className)}
    >
      <span className="inline-flex items-center gap-1.5">
        <Calendar className="size-3.5" aria-hidden />
        <time dateTime={post.date}>{post.dateLabel}</time>
      </span>
      <span aria-hidden>·</span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="size-3.5" aria-hidden />
        {post.minReadLabel}
      </span>
    </TypographyCaption>
  );
}
